import { Card, Page, Text, usePlatformTheme } from '@ui/themed';
import { t } from 'i18next';
import { useCallback, useMemo, useState } from 'react';
import { ErrorCard } from '../../../components/error-card';
import { useEnv } from '../../../context/env-context';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { SetupBanner } from '../../onboarding';
import { useAllPriceListsWithManualPriceCounts } from '../../price-lists';
import { ColumnPickerContent } from '../components/column-picker';
import { CustomerGroupEmptyState } from '../components/customer-group-empty-state';
import { CustomerGroupsBaseCard } from '../components/customer-groups-base-card';
import { CustomerGroupsResourcesCard } from '../components/customer-groups-resources-card';
import { CustomerGroupsTable } from '../components/customer-groups-table';
import { CustomerGroupsToolsCard } from '../components/customer-groups-tools-card';
import { type ColumnId, CUSTOMER_GROUP_BASE_SLUG, TABLE_COLUMNS } from '../constants';
import {
  CUSTOMER_GROUP_CREATE_METHOD_PATH_IDS,
  useLastCustomerGroupCreateMethod,
} from '../create-method';
import { useCustomerGroups } from '../hooks/use-customer-groups';
import { buildHierarchialGroup } from '../transformers';

export const CustomerGroupsPage = withPageProviders(CustomerGroupsContent);

const ALL_COLUMN_IDS = new Set(TABLE_COLUMNS.map((col) => col.id));
const ALWAYS_VISIBLE_COLUMN_IDS = new Set(
  TABLE_COLUMNS.filter((col) => col.alwaysVisible).map((col) => col.id),
);
const VISIBLE_COLUMNS_STORAGE_KEY = 'customer-groups-visible-columns';

function CustomerGroupsContent() {
  const env = useEnv();
  const { navigationHandler } = useHandlers();
  const theme = usePlatformTheme();
  const [lastCustomerGroupCreateMethod] = useLastCustomerGroupCreateMethod();

  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnId>>(() => {
    const stored = localStorage.getItem(VISIBLE_COLUMNS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as ColumnId[];
      const valid = parsed.filter((id) => ALL_COLUMN_IDS.has(id));
      if (valid.length > 0) return new Set([...ALWAYS_VISIBLE_COLUMN_IDS, ...valid]);
    }
    return new Set(ALL_COLUMN_IDS);
  });

  const toggleColumn = useCallback((columnId: ColumnId) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(columnId)) {
        next.delete(columnId);
      } else {
        next.add(columnId);
      }
      localStorage.setItem(VISIBLE_COLUMNS_STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  }, []);

  const {
    data: customerGroups,
    isError: isCustomerGroupsError,
    isLoading: isLoadingCustomerGroups,
    refetch: refetchCustomerGroups,
  } = useCustomerGroups();

  const {
    data: priceLists,
    isError: isPriceListsError,
    isLoading: isLoadingPriceLists,
    refetch: refetchPriceLists,
  } = useAllPriceListsWithManualPriceCounts();

  const hierarchicalGroups = useMemo(
    () => buildHierarchialGroup(customerGroups, priceLists),
    [customerGroups, priceLists],
  );

  const baseGroup = hierarchicalGroups.find((group) => group.slug === CUSTOMER_GROUP_BASE_SLUG);
  if (!isLoadingCustomerGroups && !baseGroup) {
    throw new Error(`Base group with slug "${CUSTOMER_GROUP_BASE_SLUG}" not found`);
  }

  const inheritedGroups = hierarchicalGroups.filter(
    (group) => group.slug !== CUSTOMER_GROUP_BASE_SLUG,
  );

  const isBigCommerce = env.platform === 'bigcommerce';
  // BigCommerce does not support creating custom groups, so we hide the create button and related empty state actions for that platform
  const allowCreatingCustomerGroups = !isBigCommerce;

  return (
    <Page
      fillHeight
      title={t('features:customer_group.pages.customer_groups_page.header.title')}
      primaryAction={
        allowCreatingCustomerGroups
          ? {
              label: t('features:customer_group.pages.customer_groups_page.header.actions.create'),
              onClick: () =>
                navigationHandler.navigateById(
                  CUSTOMER_GROUP_CREATE_METHOD_PATH_IDS[lastCustomerGroupCreateMethod],
                ),
            }
          : undefined
      }
      description={t('features:customer_group.pages.customer_groups_page.header.description')}
      learnMoreUrl="https://docs.sparklayer.io/customers"
      footerDescription={t('features:customer_group.pages.customer_groups_page.footer.description')}
    >
      <SetupBanner showLaunchPrompt={false} />
      <CustomerGroupsBaseCard baseGroup={baseGroup} isLoading={isLoadingCustomerGroups} />
      {isCustomerGroupsError || isPriceListsError ? (
        <ErrorCard
          onReload={() => {
            if (isCustomerGroupsError) {
              void refetchCustomerGroups();
            }
            if (isPriceListsError) {
              void refetchPriceLists();
            }
          }}
        />
      ) : inheritedGroups.length === 0 && !isLoadingCustomerGroups ? (
        allowCreatingCustomerGroups ? (
          <CustomerGroupEmptyState
            onClick={() =>
              navigationHandler.navigateById(
                CUSTOMER_GROUP_CREATE_METHOD_PATH_IDS[lastCustomerGroupCreateMethod],
              )
            }
          />
        ) : null
      ) : (
        <Card>
          <Card.Header
            headingLevel="h2"
            primaryAction={{
              label: t('buttons:columns'),
              icon: 'viewColumns',
              disabled: isLoadingCustomerGroups,
              popoverContent: (
                <ColumnPickerContent
                  visibleColumns={visibleColumns}
                  onToggleColumn={toggleColumn}
                />
              ),
            }}
          >
            {t(
              'features:customer_group.pages.customer_groups_page.additional_customer_groups_card.header',
            )}
          </Card.Header>
          <Card.Body>
            <Text variant="secondary">
              {t(
                'features:customer_group.pages.customer_groups_page.additional_customer_groups_card.description',
              )}
            </Text>
            <CustomerGroupsTable
              inheritedGroups={inheritedGroups}
              loading={isLoadingCustomerGroups || isLoadingPriceLists}
              visibleColumns={visibleColumns}
            />
          </Card.Body>
        </Card>
      )}

      {/*  Remove direct links into the dashboard from Shopify on primary workflows */}
      {theme !== 'polaris' && (
        <>
          <CustomerGroupsToolsCard />
          <CustomerGroupsResourcesCard />
        </>
      )}
    </Page>
  );
}
